// A deliberately tiny Markdown reader for TEXT-ONLY rendering (headings, bullets, bold, paragraphs).
// It never produces HTML: React renders MdInline.text as text nodes. Links collapse to their label.
// Linear-time: lines over MAX_LINE_REGEX_CHARS are kept as plain text and never matched.
import { MAX_LINE_REGEX_CHARS } from "./sanitize.ts";

export type MdInline = { text: string; bold: boolean };
export type MdBlock =
  | { kind: "heading"; level: 1 | 2 | 3; inlines: MdInline[] }
  | {
      kind: "bullet";
      depth: number;
      checked: boolean | null;
      inlines: MdInline[];
    }
  | { kind: "paragraph"; inlines: MdInline[] };

function appendInline(out: MdInline[], text: string, bold: boolean): void {
  const last = out.at(-1);
  if (last && last.bold === bold)
    out[out.length - 1] = { text: last.text + text, bold };
  else out.push({ text, bold });
}

const isAlnum = (c: string | undefined) =>
  c !== undefined && /[\p{L}\p{N}]/u.test(c);

/** "__x__" emphasis at word boundaries only (CommonMark): opener not after a letter/digit, closer not before one. Linear scan. */
export function splitUnderscoreEmphasis(s: string): MdInline[] {
  const out: MdInline[] = [];
  let plainStart = 0;
  let i = 0;
  while (i < s.length) {
    if (s[i] === "_" && s[i + 1] === "_" && !isAlnum(s[i - 1])) {
      const next = s.indexOf("_", i + 2);
      if (
        next > i + 2 &&
        s[next + 1] === "_" &&
        !isAlnum(s[next + 2]) &&
        !(s[next + 2] === "." && isAlnum(s[next + 3])) // __init__.py stays literal
      ) {
        if (i > plainStart) appendInline(out, s.slice(plainStart, i), false);
        appendInline(out, s.slice(i + 2, next), true);
        i = plainStart = next + 2;
        continue;
      }
    }
    i++;
  }
  if (plainStart < s.length) appendInline(out, s.slice(plainStart), false);
  return out;
}

export function parseInlines(s: string): MdInline[] {
  if (s.length > MAX_LINE_REGEX_CHARS)
    return s ? [{ text: s, bold: false }] : [];
  const out: MdInline[] = [];
  const plain = s.replace(/\[([^\]]*)\]\((?:[^()]|\([^()]*\))*\)/g, "$1");
  for (const part of plain.split(/(\*\*[^*]+\*\*)/g)) {
    if (!part) continue;
    if (/^\*\*.+\*\*$/.test(part)) appendInline(out, part.slice(2, -2), true);
    else
      for (const x of splitUnderscoreEmphasis(part))
        appendInline(out, x.text, x.bold);
  }
  return out;
}

const HEADING = /^(#{1,6})[ \t]+(.*)$/;
const BULLET = /^(\s*)(?:[-*+•]|\d{1,3}[.)])\s+(?:\[([ xX])\]\s+)?(.*)$/;

/** Joins the lines (so bold may span a line break) when the result stays within the cap; else parses line by line. */
function paragraphInlines(lines: string[]): MdInline[] {
  const joined = lines.join(" ");
  if (joined.length <= MAX_LINE_REGEX_CHARS) return parseInlines(joined);
  const out: MdInline[] = [];
  lines.forEach((line, i) => {
    const inlines = parseInlines(line);
    if (i > 0 && inlines.length)
      inlines[0] = { ...inlines[0], text: ` ${inlines[0].text}` };
    for (const x of inlines) appendInline(out, x.text, x.bold);
  });
  return out;
}

export function markdownToBlocks(md: string): MdBlock[] {
  const blocks: MdBlock[] = [];
  let para: string[] = [];
  const flush = () => {
    if (para.length)
      blocks.push({ kind: "paragraph", inlines: paragraphInlines(para) });
    para = [];
  };
  for (const line of md.split("\n")) {
    if (!line.trim()) {
      flush();
      continue;
    }
    if (line.length <= MAX_LINE_REGEX_CHARS) {
      const h = HEADING.exec(line);
      if (h) {
        flush();
        const level = Math.min(3, h[1].length) as 1 | 2 | 3;
        blocks.push({
          kind: "heading",
          level,
          inlines: parseInlines(h[2].trim()),
        });
        continue;
      }
      const b = BULLET.exec(line);
      if (b) {
        flush();
        const depth = Math.min(
          3,
          Math.floor(b[1].replace(/\t/g, "  ").length / 2),
        );
        const checked = b[2] === undefined ? null : b[2].toLowerCase() === "x";
        blocks.push({
          kind: "bullet",
          depth,
          checked,
          inlines: parseInlines(b[3]),
        });
        continue;
      }
    }
    para.push(line.trim());
  }
  flush();
  return blocks;
}

export const markdownToPlainText = (md: string): string =>
  markdownToBlocks(md)
    .map((b) => b.inlines.map((i) => i.text).join(""))
    .join("\n");
